/*============================================================================
  Cart drawer / cart page: discount code input and totals
  - Applies / removes discount codes via the Cart AJAX API
  - Renders the applied codes as badges from cart.discount_codes, Liquid's
    discount_applications do not include shipping codes
  - Re-renders the totals together with the rest of the cart
  Kept outside of theme.js so the vendor theme file stays untouched.
==============================================================================*/
(function() {
  // After theme.CartForm rebuilds the cart from cart.ajax.liquid,
  // replace the totals with the fresh markup as well.
  // theme.js is loaded before this file (both deferred), so patch right away.
  if (window.theme?.CartForm) {
    const cartMarkup = theme.CartForm.prototype.cartMarkup;

    theme.CartForm.prototype.cartMarkup = function(text) {
      cartMarkup.call(this, text);

      const html = document.createElement('div');
      html.innerHTML = text;

      const totals = html.querySelector('.cart__totals');
      if (totals) {
        this.form.querySelector('[data-cart-totals]')?.replaceChildren(totals);
      }
    };
  }

  // Replaces all discount codes on the cart: comma separated, empty string removes them
  async function updateDiscount(codes) {
    const response = await fetch(theme.routes.cartPage + '/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: codes })
    });

    if (!response.ok) {
      throw new Error('Cart update failed: ' + response.status);
    }

    const cart = await response.json();

    // theme.CartForm listens to cart:build and re-renders the cart
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

    return cart;
  }

  // Script is deferred, so the DOM is already parsed here
  const container = document.querySelector('[data-discount-code]');
  if (!container) return;

  const input = container.querySelector('[data-discount-input]');
  const applyBtn = container.querySelector('[data-discount-apply]');
  const error = container.querySelector('[data-discount-error]');
  const applied = container.querySelector('[data-discount-applied]');
  const template = container.querySelector('[data-discount-tag]');
  const strings = container.dataset;

  // Codes currently applied to the cart, kept in sync by renderCodes()
  let appliedCodes = [];

  function renderCodes(cart) {
    appliedCodes = (cart.discount_codes || []).filter(d => d.applicable).map(d => d.code);

    applied.replaceChildren(...appliedCodes.map(code => {
      const tag = template.content.firstElementChild.cloneNode(true);
      tag.querySelector('[data-discount-tag-code]').textContent = code;
      tag.querySelector('[data-discount-remove]').dataset.discountRemove = code;
      return tag;
    }));
  }

  function setError(message = '') {
    error.textContent = message;
    error.classList.toggle('hide', !message);
  }

  // Apply and remove both rewrite the cart's codes, so only one request may run at a time
  let busy = false;

  async function run(el, loadingClass, action) {
    if (busy) return;
    busy = true;

    setError();
    el.classList.add(loadingClass);
    try {
      await action();
    } catch (e) {
      console.error('Discount code error:', e);
      setError(strings.errorGeneric);
    } finally {
      el.classList.remove(loadingClass);
      busy = false;
    }
  }

  function apply() {
    const code = input.value.trim();
    if (!code) {
      setError(strings.errorEmpty);
      return;
    }

    const previousCodes = appliedCodes.join(',');

    run(applyBtn, 'btn--loading', async () => {
      const cart = await updateDiscount(code);

      if (cart.discount_codes?.some(d => d.applicable && d.code.toLowerCase() === code.toLowerCase())) {
        input.value = '';
        return;
      }

      // A non-applicable code stays on the cart, so put back the previous valid ones
      await updateDiscount(previousCodes);
      setError(strings.errorInvalid);
    });
  }

  // Quantity changes (theme.js) and updateDiscount() both send the new cart.
  // theme.js also sends the error response of a failed change (422), skip that one
  document.addEventListener('cart:updated', evt => {
    const cart = evt.detail?.cart;
    if (Array.isArray(cart?.discount_codes)) {
      renderCodes(cart);
    }
  });

  // Initial render, Liquid cannot output shipping codes
  fetch(theme.routes.cartPage + '.js')
    .then(response => response.json())
    .then(renderCodes)
    .catch(e => console.error('Discount code error:', e));

  applyBtn.addEventListener('click', apply);

  // Enter inside the cart form would submit it and go to checkout
  input.addEventListener('keydown', evt => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
      apply();
    }
  });

  input.addEventListener('input', () => setError());

  // Badges are re-rendered with every cart update, so listen on the wrapper
  applied.addEventListener('click', evt => {
    const removeBtn = evt.target.closest('[data-discount-remove]');
    if (removeBtn) {
      const remaining = appliedCodes.filter(code => code !== removeBtn.dataset.discountRemove);
      run(applied, 'is-loading', () => updateDiscount(remaining.join(',')));
    }
  });
})();
